import React, { useRef, useState } from "react";
import { useQuery } from "@apollo/client";
import DownloadIcon from "@mui/icons-material/Download";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { GET_GALLERY_PAIRS } from "../../../../utils/GraphQL/queries";
import BdrButton from "../../../../components/BdrButton/BdrButton";
import BdrModal from "../../../../components/BdrModal/BdrModal";
import SmartImage from "../../../../components/SmartImage/SmartImage";
import AdminBDRSpinner from "../AdminBDRSpinner/AdminBDRSpinner";
import { baImageUrl } from "../../../../utils/baImageUrl";
import { FETCH_IMAGE_OPTS } from "../../../../utils/fetchImageForCanvas";
import { useSnackbar } from "../../../../utils/GlobalSnackbar/GlobalSnackbarContext";
import useNavHeightVar from "../../../../utils/useNavHeightVar";
import "./galleryPairs.css";

/**
 * Gallery Pairs — Image Groups tab (admin only).
 *
 * Gallery-approved before/after groups with BOTH photos set, newest first, 40
 * a page, for picking photos for advertising. The server owns the filter and
 * the page size (getGalleryPairs).
 *
 * THUMBNAILS EVERYWHERE ON SCREEN, ORIGINALS ONLY ON DOWNLOAD. The grid and
 * the modal both render s3ThumbUrl (400x533). The full-resolution original is
 * fetched only when Download is pressed, so browsing 40 pairs never pulls 80
 * originals. The download lives in this tool's own modal and nowhere else.
 *
 * The bytes come through `fetch` with FETCH_IMAGE_OPTS (CORS, no-store), never
 * a crossOrigin <img> — see utils/fetchImageForCanvas.js for the cache trap.
 */

const PAIR_SIDES = [
  { key: "beforeId", label: "Before", file: "before" },
  { key: "afterId", label: "After", file: "after" },
];

const EXT_BY_TYPE = {
  "image/jpeg": "jpg",
  "image/png": "png",
  "image/webp": "webp",
  "image/heic": "heic",
};

// Gap between the two saves. Two anchor downloads fired in the same tick can
// lose the second one in Chrome; a short pause keeps both.
const SECOND_SAVE_DELAY_MS = 400;

const toDate = (dateEntered) => {
  const d = new Date(Number(dateEntered));
  return Number.isNaN(d.getTime()) ? null : d;
};

const formatDay = (dateEntered) =>
  toDate(dateEntered)?.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }) ?? "";

// Local calendar day as YYYY-MM-DD, built from its parts (never toISOString,
// which is UTC and would name an evening job by the next day).
const fileDay = (dateEntered) => {
  const d = toDate(dateEntered);
  if (!d) return "undated";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const slug = (text) =>
  String(text || "")
    .trim()
    .replace(/[^A-Za-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

// BDR_2026-10-12_Golden-Oak_a6e4cd — the id tail keeps two same-day,
// same-stain pairs from overwriting each other in the Downloads folder.
const pairFileBase = (group) =>
  ["BDR", fileDay(group.dateEntered), slug(group.stainColor), String(group._id).slice(-6)]
    .filter(Boolean)
    .join("_");

const thumbSrc = (photo) => photo?.s3ThumbUrl;

// The ORIGINAL — no variant. /ba-image is the same-origin fallback for a
// photo the server returned no CDN url for; it redirects to the original.
const originalSrc = (photo) => photo?.s3ImageUrl ?? baImageUrl(photo?._id);

const fetchOriginal = async (photo) => {
  const res = await fetch(originalSrc(photo), FETCH_IMAGE_OPTS);
  if (!res.ok) throw new Error(`HTTP ${res.status} for photo ${photo?._id}`);
  return res.blob();
};

const saveBlob = (blob, fileName) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Revoking in the same tick can cancel the download before it starts.
  setTimeout(() => URL.revokeObjectURL(url), 10000);
};

const isComplete = (group) => Boolean(group?.beforeId && group?.afterId);

// Spans, not divs: in the grid this renders inside the card's <button>, which
// only allows phrasing content.
const PairThumb = ({ photo, label, className }) => (
  <span className={`gp-thumb-wrap ${className || ""}`}>
    {photo ? (
      <SmartImage
        className="gp-thumb"
        url={thumbSrc(photo)}
        photoId={photo._id}
        variant="thumb"
        alt={label}
        loading="lazy"
      />
    ) : (
      <span className="gp-thumb gp-thumb-missing">Unavailable</span>
    )}
    <span className="gp-thumb-tag">{label}</span>
  </span>
);

const Pager = ({ page, lastPage, loading, onPage }) => (
  <div className="gp-pager">
    <BdrButton
      size="sm"
      variant="action"
      icon={<ChevronLeftIcon fontSize="small" />}
      collapseToIcon
      disabled={loading || page <= 1}
      onClick={() => onPage(page - 1)}
    >
      Newer
    </BdrButton>
    <span className="gp-pager-label">
      Page {page} of {lastPage}
    </span>
    <BdrButton
      size="sm"
      variant="action"
      icon={<ChevronRightIcon fontSize="small" />}
      collapseToIcon
      disabled={loading || page >= lastPage}
      onClick={() => onPage(page + 1)}
    >
      Older
    </BdrButton>
  </div>
);

const GalleryPairs = () => {
  const [page, setPage] = useState(1);
  const [openGroup, setOpenGroup] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const topRef = useRef(null);
  const { handleSnack } = useSnackbar();
  // Feeds .gp-root's scroll-margin so a page change doesn't land the toolbar
  // under the sticky navbar.
  useNavHeightVar();

  const { data, previousData, loading, error } = useQuery(GET_GALLERY_PAIRS, {
    variables: { page },
    fetchPolicy: "cache-and-network",
  });

  // Keep the last page on screen while the next one loads, so the grid does
  // not collapse to nothing and jump the scroll position.
  const result = data?.getGalleryPairs ?? previousData?.getGalleryPairs;
  const groups = result?.groups ?? [];
  const totalCount = result?.totalCount;
  // The server clamps a page past the end, so its answer is the real page.
  const currentPage = result?.page ?? page;
  const lastPage = result ? Math.max(1, Math.ceil(totalCount / result.pageSize)) : 1;

  const goToPage = (next) => {
    setPage(next);
    topRef.current?.scrollIntoView({ block: "start", behavior: "smooth" });
  };

  const downloadPair = async (group) => {
    if (!isComplete(group)) return;
    setDownloading(true);
    try {
      // Both fetched BEFORE either is saved: a failure on one side never
      // leaves half a pair in the Downloads folder.
      const blobs = await Promise.all(
        PAIR_SIDES.map((side) => fetchOriginal(group[side.key]))
      );
      const base = pairFileBase(group);
      for (let i = 0; i < PAIR_SIDES.length; i++) {
        if (i > 0) {
          await new Promise((resolve) => setTimeout(resolve, SECOND_SAVE_DELAY_MS));
        }
        const ext = EXT_BY_TYPE[blobs[i].type] || "jpg";
        saveBlob(blobs[i], `${base}_${PAIR_SIDES[i].file}.${ext}`);
      }
      handleSnack("Saved the before and after originals", 2500);
    } catch (err) {
      console.error("[GalleryPairs.downloadPair] failed", { groupId: group?._id, err });
      handleSnack("Couldn't download the photos", 3000);
    } finally {
      setDownloading(false);
    }
  };

  const pagerProps = { page: currentPage, lastPage, loading, onPage: goToPage };

  return (
    <div className="gp-root" ref={topRef}>
      {loading && <AdminBDRSpinner message="Loading pairs..." />}

      {error && (
        <div className="bdr-alert bdr-alert--danger bdr-alert--start" role="alert">
          {error.message?.includes("Forbidden")
            ? "Forbidden — admin role required."
            : `Couldn't load the gallery pairs: ${error.message}`}
        </div>
      )}

      {result && (
        <>
          <div className="gp-toolbar">
            <p className="gp-summary">
              {totalCount.toLocaleString("en-US")} gallery pair
              {totalCount === 1 ? "" : "s"} with both photos, newest first.
              Open one to download the originals.
            </p>
            {lastPage > 1 && <Pager {...pagerProps} />}
          </div>

          {groups.length === 0 ? (
            <div className="bdr-empty">No complete gallery pairs yet.</div>
          ) : (
            <div className="gp-grid">
              {groups.map((group) => (
                <BdrButton
                  key={group._id}
                  variant="plain"
                  className="gp-card"
                  ariaLabel={`Open ${group.stainColor || "pair"} from ${formatDay(group.dateEntered)}`}
                  onClick={() => setOpenGroup(group)}
                >
                  <span className="gp-card-pair">
                    {PAIR_SIDES.map((side) => (
                      <PairThumb
                        key={side.key}
                        photo={group[side.key]}
                        label={side.label}
                      />
                    ))}
                  </span>
                  <span className="gp-card-meta">
                    <span className="gp-card-stain">
                      {group.stainColor || "No stain listed"}
                    </span>
                    <span className="gp-card-date">{formatDay(group.dateEntered)}</span>
                  </span>
                </BdrButton>
              ))}
            </div>
          )}

          {lastPage > 1 && groups.length > 0 && (
            <div className="gp-footer">
              <Pager {...pagerProps} />
            </div>
          )}
        </>
      )}

      <BdrModal
        open={Boolean(openGroup)}
        onClose={() => setOpenGroup(null)}
        size="lg"
        variant="panel"
        title={
          openGroup
            ? `${openGroup.stainColor || "Gallery pair"} · ${formatDay(openGroup.dateEntered)}`
            : ""
        }
        actions={
          <>
            <BdrButton onClick={() => setOpenGroup(null)}>Close</BdrButton>
            <BdrButton
              variant="action-solid"
              icon={<DownloadIcon fontSize="small" />}
              disabled={downloading || !isComplete(openGroup)}
              onClick={() => downloadPair(openGroup)}
            >
              {downloading ? "Downloading…" : "Download pair"}
            </BdrButton>
          </>
        }
      >
        {openGroup && (
          <div className="gp-modal">
            <div className="gp-modal-pair">
              {PAIR_SIDES.map((side) => (
                <PairThumb
                  key={side.key}
                  photo={openGroup[side.key]}
                  label={side.label}
                  className="gp-modal-thumb"
                />
              ))}
            </div>
            <dl className="gp-modal-meta">
              <div className="gp-modal-meta-row">
                <dt className="gp-modal-meta-term">Refinished by</dt>
                <dd className="gp-modal-meta-value">
                  {openGroup.refinishers?.join(", ") || "—"}
                </dd>
              </div>
              <div className="gp-modal-meta-row">
                <dt className="gp-modal-meta-term">Stain</dt>
                <dd className="gp-modal-meta-value">{openGroup.stainColor || "—"}</dd>
              </div>
            </dl>
            <p className="gp-modal-note">
              {isComplete(openGroup)
                ? "Shown at preview size. Download saves both full-resolution originals as separate files."
                : "One of this pair's photos is no longer available, so it can't be downloaded."}
            </p>
          </div>
        )}
      </BdrModal>
    </div>
  );
};

export default GalleryPairs;
